import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import { Alert } from "@mui/material";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";
import MDEditor from "components/MDEditor";

import useScenarioEventsData from "../data/useScenarioEventsData";
import useScenarioRoutesData from "../data/useScenarioRoutesData";
import useScenarioAreasData from "../data/useScenarioAreasData";
import { disableTime } from "rsuite/esm/internals/utils/date";

function ScenarioForm({
  value,
  onChange,
  metadata,
  onSubmit,
  onMonitoringItemsChange,
  routesData = [],
  areasData = [],
}) {
  const { t } = useTranslation();
  const {
    category_id,
    name,
    description,
    start_date,
    end_date,
    kpi_id,
    kpi_option_id,
    kpi_monitoring_ids,
    type_ids,
    location,
    action_id,
    target_ids,
    message,
  } = value;

  const [errors, setErrors] = useState({});
  const [localDescription, setLocalDescription] = useState(description || "");
  const [localMessage, setLocalMessage] = useState(message || "");
  const [localName, setLocalName] = useState(name || "");

  useEffect(() => {
    setLocalName(name || "");
  }, [name]);

  const locationLabel = useMemo(() => {
    if (!location) return "";

    if (location.type === "Point" && Array.isArray(location.coordinates)) {
      const [lng, lat] = location.coordinates;
      return t("scenarios.create.locationPoint", { lng: lng.toFixed(5), lat: lat.toFixed(5) });
    }

    if (location.type === "LineString" && Array.isArray(location.coordinates)) {
      const pointsCount = location.coordinates.length;
      return t("scenarios.create.locationLine", { count: pointsCount });
    }

    return t("scenarios.create.locationSelected");
  }, [location, t]);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    onChange({ [name]: value });

    setErrors((prev) => {
      const copy = { ...prev };
      if (value) delete copy[name];
      return copy;
    });
  };

  const handleDescriptionChange = (val) => {
    setLocalDescription(val || "");
  };

  const handleMessageChange = (val) => {
    setLocalMessage(val || "");
  };

  const handleDateChange = (dateArray, field) => {
    if (!dateArray || !dateArray[0]) return;

    const iso = dateArray[0].toISOString();
    onChange({ [field]: iso });

    setErrors((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };

  const handleLocationClear = () => {
    onChange({ location: null, location_path_ids: [] });
  };

  const handleMetaSelect = (field) => (_, selectedOption) => {
    const id = selectedOption?.id || null;
    // Reset everything that depends on the category
    if (field === "category_id") {
      const shouldHideDates = id === 2;
      onChange({
        category_id: id,
        kpi_id: null,
        kpi_option_id: null,
        kpi_monitoring_ids: [],
        type_ids: null,
        start_date: shouldHideDates ? null : null,
        end_date: shouldHideDates ? null : null,

      });


      setErrors((prev) => {
        const copy = { ...prev };
        if (id) delete copy.category_id;
        delete copy.kpi_id;
        delete copy.kpi_option_id;
        delete copy.start_date;
        delete copy.end_date;
        return copy;
      });

      return;
    }

    // Reset everything that depends on the KPI
    if (field === "kpi_id") {
      
      onChange({
        kpi_id: id,
        kpi_option_id: null,
        kpi_monitoring_ids: [],
        type_ids: id >1? selectedOption?.type_id: null,
        start_date: null,
        end_date: null,
      });

      setErrors((prev) => {
        const copy = { ...prev };
        if (id) delete copy.kpi_id;
        delete copy.kpi_option_id;
        return copy;
      });

      return;
    }

    // Default behavior for metadata fields
    onChange({ [field]: id });

    setErrors((prev) => {
      const copy = { ...prev };
      if (id) delete copy[field];
      return copy;
    });
  };

  const handleTargetsChange = (_, selectedOptions) => {
    const ids = selectedOptions.map((opt) => opt.id);
    onChange({ target_ids: ids });
  };

  const categoryOptions = metadata?.categories || [];

  const kpiOptions = useMemo(
    () =>
      (metadata?.kpis || []).filter(
        (k) => !category_id || k.category_id === category_id
      ),
    [metadata, category_id]
  );

  const selectedCategory =
    categoryOptions.find((cat) => cat.id === category_id) || null;

  const selectedKpi =
    (metadata?.kpis || []).find((k) => k.id === kpi_id) || null;

  const kpiOptionOptions = selectedKpi?.options || [];
  const selectedKpiOption =
    kpiOptionOptions.find((opt) => opt.id === kpi_option_id) || null;

  const actionOptions = metadata?.actions || [];
  const selectedAction =
    actionOptions.find((act) => act.id === 1) || null;

  const targetOptions = selectedAction?.targets || [];
  const selectedTargets = targetOptions.filter((t) =>
    target_ids?.includes(t.id)
  );


  const monitoringConfig = selectedKpi?.monitoring;
  const monitoringType = monitoringConfig?.type; // "events" | "areas" | "routes"
  const monitoringSelection = monitoringConfig?.selection; // "single" | "multi"

  // Events
  const {
    events: eventOptions,
    loading: loadingEvents,
  } = useScenarioEventsData(selectedKpi);

  // Routes
  const {
    routes: fetchedRouteOptions,
    loading: loadingRoutes,
  } = useScenarioRoutesData(selectedKpi, routesData);
  const routeOptions = routesData.length ? routesData : fetchedRouteOptions;

  // Areas
  const { areas: fetchedAreaOptions, loading: loadingAreas } = useScenarioAreasData(
    selectedKpi,
    areasData
  );
  const areaOptions = areasData.length ? areasData : fetchedAreaOptions;

  useEffect(() => {
    if (!onMonitoringItemsChange) return;

    let items = [];

    if (monitoringType === "events") {
      items = eventOptions.map((e) => ({
        ...e,
        monitoringType: "event",
      }));
    } else if (monitoringType === "routes") {
      items = routeOptions.map((r) => ({
        ...r,
        monitoringType: "route",
      }));
    } else if (monitoringType === "areas") {
      items = areaOptions.map((a) => ({
        ...a,
        monitoringType: "area",
      }));
    }

    if (monitoringType === "areas") {
      console.log("ScenarioForm areas emission", {
        monitoringType,
        areaOptionsCount: areaOptions.length,
        firstArea: areaOptions[0] || null,
      });
    }

    onMonitoringItemsChange({
      type: monitoringType,
      items,
    });
  }, [
    monitoringType,
    eventOptions,
    routeOptions,
    areaOptions,
    onMonitoringItemsChange,
  ]);

  // change date based on event
  useEffect(() => {
    if (monitoringType !== "events") return;
        if (category_id === 2 || category_id === 3) return; // ✅ if dates are hidden, don’t auto-set them

    const selectedEventId = kpi_monitoring_ids?.[0];
    if (!selectedEventId) return;

    const selectedEvent = eventOptions.find((e) => e.id === selectedEventId);
    if (!selectedEvent) return;

    const startFromEvent =
      selectedEvent.valid_from ?? selectedEvent.validFrom ?? null;
    const endFromEvent =
      selectedEvent.valid_until ?? selectedEvent.validUntil ?? null;

    if (start_date === startFromEvent && end_date === endFromEvent) return;

    onChange({
      start_date: startFromEvent,
      end_date: endFromEvent,
    });
  }, [
    monitoringType,
    kpi_monitoring_ids,
    category_id,
    eventOptions,
    start_date,
    end_date,
    onChange,
  ]);

  const validate = () => {
    const newErrors = {};

    if (!name) newErrors.name = t("scenarios.create.nameRequired");
    if (!category_id) newErrors.category_id = t("scenarios.create.categoryRequired");
    if (!kpi_id) newErrors.kpi_id = t("scenarios.create.kpiRequired");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = (e) => {
    e.preventDefault();

    // sync editor fields back to form state if you need it
    onChange({
      description: localDescription,
      message: localMessage,
    });

    const isValid = validate();
    if (!isValid) return;

    const payload = {
      category_id,
      name,
      description: localDescription || "",
      start_date: category_id === 2 || category_id === 3 ? null : start_date || null, // ✅ enforce clearance
      end_date: category_id === 2 || category_id === 3 ? null : end_date || null, // ✅ enforce clearance

      kpi_id,
      kpi_option_id: kpi_option_id ?? null,
      kpi_monitoring_ids: kpi_monitoring_ids ?? [],
      type_ids: type_ids ?? null,

      location: location ?? null,
      location_path_ids: value.location_path_ids ?? [],

      action_id:1,
      target_ids: target_ids ?? [],

      message: localMessage || "",
    };

    if (onSubmit) {
      onSubmit(payload);
    } else {
      console.log("Scenario payload:", payload);
    }
  };
    const hideDates = category_id === 2 || category_id === 3;

  return (
    <MDBox component="form" pb={3} px={3} onSubmit={(e) => e.preventDefault()}>
      <Grid container spacing={3}>
        {/* Category */}
        <Grid item xs={12}>
          <MDTypography
            component="label"
            variant="button"
            fontWeight="regular"
            color="text"
          >
            {t("scenarios.create.category")}
          </MDTypography>
          <Autocomplete
            value={selectedCategory}
            options={categoryOptions}
            getOptionLabel={(option) => option.name}
            onChange={handleMetaSelect("category_id")}
            renderInput={(params) => (
              <MDInput
                {...params}
                variant="standard"
                error={!!errors.category_id}
              />
            )}
          />
          {errors.category_id && (
            <MDTypography variant="caption" color="error">
              {errors.category_id}
            </MDTypography>
          )}
        </Grid>

        {/* Scenario Name */}
        <Grid item xs={12}>
          <MDTypography
            component="label"
            variant="button"
            fontWeight="regular"
            color="text"
          >
            {t("scenarios.create.scenarioName")}
          </MDTypography>
          <MDInput
            fullWidth
            name="name"
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={() => onChange({ name: localName })}
            variant="standard"
            error={!!errors.name}
            autoComplete="off"
          />

          {errors.name && (
            <MDTypography variant="caption" color="error">
              {errors.name}
            </MDTypography>
          )}
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <MDTypography
            component="label"
            variant="button"
            fontWeight="regular"
            color="text"
          >
            {t("scenarios.create.description")}
          </MDTypography>
          <MDEditor
            defaultValue={localDescription}
            onChange={handleDescriptionChange}
          />
        </Grid>

        {/* Start / End Date */}
      {/* ✅ Start / End Date (hidden when category_id === 2 || category_id === 3) */}
        {!hideDates && (
          <>
            <Grid item xs={12} sm={6}>
              <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                {t("scenarios.create.startDate")}
              </MDTypography>
              <MDBox sx={{ width: "100%" }}>
                <MDDatePicker
                  input={{ placeholder: t("scenarios.create.selectStartDateTime") }}
                  value={start_date ? new Date(start_date) : null}
                  options={{
                    enableTime: true,
                     allowInput: false,
                    dateFormat: "d/m/Y H:i",
                    altFormat: "d/m/Y H:i",
                    time_24hr: true,
                    clickOpens: false,
                  }}
                  onChange={(date) => handleDateChange(date, "start_date")}
                  
                />
              </MDBox>
            </Grid>

            <Grid item xs={12} sm={6}>
              <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                {t("scenarios.create.endDate")}
              </MDTypography>
              <MDBox sx={{ width: "100%" }}>
                <MDDatePicker
                  input={{ placeholder: t("scenarios.create.selectEndDateTime")}}
                  value={end_date ? new Date(end_date) : null}
                  options={{
                    enableTime: true,
                    dateFormat: "d/m/Y H:i",
                    altFormat: "d/m/Y H:i",
                    time_24hr: true,
                    clickOpens: false,
                  }}
                  onChange={(date) => handleDateChange(date, "end_date")}
                />
              </MDBox>
            </Grid>
          </>
        )}

        {/* KPI */}
        <Grid item xs={12}>
          <MDTypography
            component="label"
            variant="button"
            fontWeight="regular"
            color="text"
          >
            {t("scenarios.create.kpi")}
          </MDTypography>
          <Autocomplete
            value={selectedKpi}
            options={kpiOptions}
            getOptionLabel={(option) => option.name}
            onChange={handleMetaSelect("kpi_id")}
            renderInput={(params) => (
              <MDInput {...params} variant="standard" error={!!errors.kpi_id} />
            )}
          />
          {errors.kpi_id && (
            <MDTypography variant="caption" color="error">
              {errors.kpi_id}
            </MDTypography>
          )}
        </Grid>

        {/* KPI Option (if options exist) */}
        {kpiOptionOptions.length > 0 && (
          <Grid item xs={12}>
            <MDTypography
              component="label"
              variant="button"
              fontWeight="regular"
              color="text"
            >
              {t("scenarios.create.kpiOption")}
            </MDTypography>
            <Autocomplete
              value={selectedKpiOption}
              options={kpiOptionOptions}
              getOptionLabel={(option) => option.label}
              onChange={handleMetaSelect("kpi_option_id")}
              renderInput={(params) => (
                <MDInput {...params} variant="standard" />
              )}
            />
          </Grid>
        )}

        {/* Monitoring: Events */}
        {monitoringType === "events" && (
          <Grid item xs={12}>
            <MDTypography
              component="label"
              variant="button"
              fontWeight="regular"
              color="text"
            >
              {t("scenarios.create.event")}
            </MDTypography>
            <Autocomplete
              loading={loadingEvents}
              value={
               
                eventOptions.find((e) =>{ 
                 return kpi_monitoring_ids?.includes(e.id)}) ||
                null
              }
              options={eventOptions}
              getOptionLabel={(option) => option.name || ""}
              onChange={(_, opt) => {
                const startFromEvent =
                  opt?.valid_from ?? opt?.validFrom ?? null;
                const endFromEvent =
                  opt?.valid_until ?? opt?.validUntil ?? null;

                const typeEventName = opt?.category?.id ?? null;
                console.log("TYPE EVENT NAME", typeEventName);
                console.log("SELECTED EVENT", opt);

                onChange({
                  ...value,
                  kpi_monitoring_ids: opt ? [opt.id] : [],
                  type_ids: typeEventName,
                  start_date: startFromEvent,
                  end_date: endFromEvent,
                  // location: opt ? opt.geometry || null : null,
                  // location_path_ids: opt ? opt.linkIds || [] : [],
                });
              }}
              renderOption={(props, option) => (
                <li
                  {...props}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  {option.icon && (
                    <img
                      src={option.icon}
                      alt=""
                      style={{ width: 22, height: 22, objectFit: "contain" }}
                    />
                  )}
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <MDInput
                  {...params}
                  variant="standard"
                  placeholder={t("scenarios.create.selectEvent")}
                />
              )}
            />
          </Grid>
        )}

        {/* Monitoring: Routes */}
        {monitoringType === "routes" && (
          <Grid item xs={12}>
            <MDTypography
              component="label"
              variant="button"
              fontWeight="regular"
              color="text"
            >
              {t("scenarios.create.routes")}
            </MDTypography>
              <Autocomplete
                loading={loadingRoutes}
                value={
                  routeOptions.find((r) =>
                    kpi_monitoring_ids?.includes(r.id)
                  ) || null
                }
                options={routeOptions}
                getOptionLabel={(option) => option.name || ""}
                onChange={(_, selected) =>
                  onChange({
                    kpi_monitoring_ids: selected ? [selected.id] : [],
                  })
                }
              renderInput={(params) => (
                <MDInput
                  {...params}
                  variant="standard"
                  placeholder={t("scenarios.create.selectRoutes")}
                />
              )}
            />
          </Grid>
        )}

        {/* Monitoring: Areas */}
        {monitoringType === "areas" && (
          <Grid item xs={12}>
            <MDTypography
              component="label"
              variant="button"
              fontWeight="regular"
              color="text"
            >
              {t("scenarios.create.areas")}
            </MDTypography>
            <Autocomplete
            loading={loadingAreas}
            value={
              areaOptions.find((a) =>
                kpi_monitoring_ids?.includes(a.id)
              ) || null
            }
            options={areaOptions}
            getOptionLabel={(option) => option.name || ""}
            onChange={(_, selected) =>
              onChange({
                kpi_monitoring_ids: selected ? [selected.id] : [],
              })
            }
              renderInput={(params) => (
                <MDInput
                  {...params}
                  variant="standard"
                  placeholder={t("scenarios.create.selectAreas")}
                />
              )}
            />
          </Grid>
        )}

        {/* Location (from map or event) */}
        <Grid item xs={12}>
          <MDTypography
            component="label"
            variant="button"
            fontWeight="regular"
            color="text"
          >
           {t("scenarios.create.targetLocation")}
          </MDTypography>

          {location ? (
            <MDBox mt={1}>
              <Alert
                severity="success"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  bgcolor: "#e7f5e9",
                  color: "#1b5e20",
                  borderRadius: "8px",
                }}
              >
                <span>
                  <strong>{t("scenarios.create.locationSelectedFromMap")}</strong> {locationLabel}
                </span>

                <MDButton
                  variant="text"
                  color="success"
                  size="small"
                  onClick={handleLocationClear}
                  sx={{ ml: 2 }}
                >
                  {t("common.buttons.clear")}
                </MDButton>
              </Alert>
            </MDBox>
          ) : (
            <MDInput
              fullWidth
              variant="standard"
              value=""
              placeholder={t("scenarios.create.noLocationSelected")}
              InputProps={{ readOnly: true }}
            />
          )}
        </Grid>


        {/* Targets (multi) */}
        <Grid item xs={12}>
          <MDTypography
            component="label"
            variant="button"
            fontWeight="regular"
            color="text"
          >
            {t("scenarios.create.targetTypes")}
          </MDTypography>
          <Autocomplete
            multiple
            value={selectedTargets}
            options={targetOptions}
            getOptionLabel={(option) => option.name}
            onChange={handleTargetsChange}
            renderInput={(params) => <MDInput {...params} variant="standard" />}
          />
        </Grid>

        {/* Message */}
        <Grid item xs={12}>
          <MDTypography
            component="label"
            variant="button"
            fontWeight="regular"
            color="text"
          >
            {t("scenarios.create.targetMessage")}
          </MDTypography>
          <MDEditor
            defaultValue={localMessage}
            onChange={handleMessageChange}
          />
        </Grid>

        {/* Save button */}
        <Grid item xs={12}>
          <MDBox mt={2} display="flex" justifyContent="flex-start">
            <MDButton variant="gradient" color="info" onClick={handleSaveClick}>
              {t("common.buttons.submit")}
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default ScenarioForm;
